import React from "react";
import { supabase } from "../supabase";
import { cachePadFile, getCachedPadFile } from "../dbSafe";

const PAD_PROXY_BASE = "https://crosstracks-stems-proxy.abryant-drury.workers.dev/api/pads/1";
const PAD_EXTS = ["m4a", "aac", "mp3"];

// Share a single AudioContext across pads/stems
let sharedCtx = null;
const getAudioCtx = () => {
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) return null;
  if (!sharedCtx) {
    sharedCtx = new AudioCtx();
  }
  return sharedCtx;
};

const FADE_TIME = 2.0;
const LOOP_OVERLAP = 1.2; // seconds of overlap for seamless crossfade loops

export function usePadPlayer({ songKey, displayKey, fadeDuration = FADE_TIME }) {
  const [accessToken, setAccessToken] = React.useState(null);
  const [currentKey, setCurrentKey] = React.useState(null);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [volume, setVolume] = React.useState(1);
  const [pan, setPan] = React.useState(1);
  const gainRef = React.useRef(null);
  const sourceRef = React.useRef(null);
  const nextSourceRef = React.useRef(null);
  const nextGainRef = React.useRef(null);
  const loopTimerRef = React.useRef(null);
  const bufferRef = React.useRef(null);
  const panNodeRef = React.useRef(null);
  const displayKeyRef = React.useRef(displayKey);

  // Auth token listener
  React.useEffect(() => {
    let mounted = true;
    const load = async () => {
      const { data } = await supabase.auth.getSession();
      if (mounted) setAccessToken(data?.session?.access_token || null);
    };
    load();
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (mounted) setAccessToken(session?.access_token || null);
    });
    return () => {
      mounted = false;
      listener?.subscription?.unsubscribe?.();
    };
  }, []);
  const resolveKey = React.useCallback((k) => {
    if (!k) return "C";
    const raw = String(k).trim();
    const match = raw.match(/^([A-Ga-g])([#b]?)(m|min)?$/i);
    if (!match) return "C";

    const letter = match[1].toUpperCase();
    const accidental = match[2] === "#" ? "#" : match[2] === "b" ? "b" : "";
    const isMinor = Boolean(match[3]);
    const SHARP_TO_FLAT = {
      "C#": "Db",
      "D#": "Eb",
      "F#": "Gb",
      "G#": "Ab",
      "A#": "Bb",
    };
    const VALID_ROOTS = ["C", "Db", "D", "Eb", "E", "F", "Gb", "G", "Ab", "A", "Bb", "B"];

    let normalized = `${letter}${accidental}`;
    normalized = SHARP_TO_FLAT[normalized] || normalized;
    const withSuffix = `${normalized}${isMinor ? "m" : ""}`;

    if (!VALID_ROOTS.includes(normalized)) return "C";
    return withSuffix;
  }, []);

  React.useEffect(() => {
    displayKeyRef.current = displayKey;
    const nextDisplay = displayKey || resolveKey(songKey);
    if (nextDisplay) {
      setCurrentKey(nextDisplay);
    }
  }, [displayKey, resolveKey, songKey]);

  // Auto-switch pad when song changes while playing
  React.useEffect(() => {
    if (!isPlaying) return;
    const targetKey = resolveKey(songKey);
    if (targetKey !== currentKey) {
      fadeToKey(targetKey, fadeDuration);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [songKey, isPlaying, fadeDuration]);

  const loadBuffer = async (key) => {
    const ctx = getAudioCtx();

    const decodeBlob = async (blob) => {
      const arr = await blob.arrayBuffer();
      return ctx.decodeAudioData(arr.slice(0));
    };

    const tryCached = async (padKey) => {
      for (const ext of PAD_EXTS) {
        const cacheKey = `pad:1:${padKey}.${ext}`;
        const cached = await getCachedPadFile(cacheKey);
        if (cached) {
          try {
            return await decodeBlob(cached);
          } catch (err) {
          }
        }
      }
      return null;
    };

    const cachedBuffer = await tryCached(key);
    if (cachedBuffer) return cachedBuffer;

    if (!accessToken) throw new Error("Not authenticated to fetch pad");
    const fetchPad = async (padKey) => {
      let lastError = null;
      for (const ext of PAD_EXTS) {
        const url = `${PAD_PROXY_BASE}?file=${padKey}.${ext}`;
        try {
          const resp = await fetch(url, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          });

          if (!resp.ok) {
            const text = await resp.text();
            throw new Error(`Failed to fetch pad ${padKey}.${ext}: ${resp.status} ${text}`);
          }

          const blob = await resp.blob();
          const buffer = await decodeBlob(blob);
          await cachePadFile(`pad:1:${padKey}.${ext}`, blob);
          return buffer;
        } catch (err) {
          lastError = err;
        }
      }
      throw lastError || new Error(`Failed to fetch pad ${padKey}`);
    };

    try {
      return await fetchPad(key);
    } catch (err) {
      if (key !== "C") {
        const cachedFallback = await tryCached("C");
        if (cachedFallback) return cachedFallback;
        return await fetchPad("C");
      }
      throw err;
    }
  };

  const clearLoopTimer = () => {
    if (loopTimerRef.current) {
      clearTimeout(loopTimerRef.current);
      loopTimerRef.current = null;
    }
  };

  const scheduleLoop = (ctx, buffer, gainNode, panNode) => {
    clearLoopTimer();
    if (!buffer) return;
    const loopDuration = buffer.duration;
    const scheduleNext = () => {
      // Start a new source slightly before the previous ends for overlap crossfade
      const overlap = Math.min(LOOP_OVERLAP, loopDuration / 2);
      const startIn = Math.max(0.01, loopDuration - overlap);
      loopTimerRef.current = setTimeout(() => {
        const now = ctx.currentTime;
        const src = ctx.createBufferSource();
        src.buffer = buffer;
        src.loop = false;

        const newGain = ctx.createGain();
        newGain.gain.value = 0;

        src.connect(newGain).connect(panNode).connect(ctx.destination);

        // Crossfade overlap: fade in new, fade out old
        newGain.gain.setValueAtTime(0, now);
        newGain.gain.linearRampToValueAtTime(gainNode.gain.value, now + overlap);

        gainNode.gain.cancelScheduledValues(now);
        gainNode.gain.setValueAtTime(gainNode.gain.value, now);
        gainNode.gain.linearRampToValueAtTime(0, now + overlap);

        src.start(now);
        // Stop old source after overlap
        if (sourceRef.current) {
          sourceRef.current.stop(now + overlap + 0.05);
        }

        // Promote new nodes to current
        sourceRef.current = src;
        gainRef.current = newGain;

        // Schedule next loop after this one
        scheduleLoop(ctx, buffer, newGain, panNode);
      }, startIn * 1000);
    };

    scheduleNext();
  };

  const startPad = async (key, fadeDuration = FADE_TIME) => {
    const ctx = getAudioCtx();
    if (!ctx) throw new Error("AudioContext unavailable");
    const buffer = await loadBuffer(key);
    bufferRef.current = buffer;

    // Prepare next source/gain for crossfade
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.loop = false;

    const gain = ctx.createGain();
    gain.gain.value = 0;

    const panner = ctx.createStereoPanner();
    panner.pan.value = pan;

    src.connect(gain).connect(panner).connect(ctx.destination);

    const now = ctx.currentTime;
    gain.gain.cancelScheduledValues(now);
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(volume, now + fadeDuration);

    src.start(now);

    // crossfade with current
    if (gainRef.current && sourceRef.current) {
      gainRef.current.gain.cancelScheduledValues(now);
      gainRef.current.gain.setValueAtTime(gainRef.current.gain.value, now);
      gainRef.current.gain.linearRampToValueAtTime(0, now + fadeDuration);
      sourceRef.current.stop(now + fadeDuration + 0.1);
    }

    gainRef.current = gain;
    panNodeRef.current = panner;
    sourceRef.current = src;
    scheduleLoop(ctx, buffer, gain, panner);
    setCurrentKey(displayKeyRef.current || key);
    setIsPlaying(true);
  };

  const fadeToKey = async (key, fadeDuration = FADE_TIME) => {
    try {
      setLoading(true);
      await startPad(key, fadeDuration);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const togglePad = async () => {
    if (!accessToken) {
      return;
    }
    if (isPlaying) {
      await stopPad();
      return;
    }
    const targetKey = resolveKey(songKey);
    try {
      setLoading(true);
      await startPad(targetKey);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const stopPad = async () => {
    if (!isPlaying) return;
    const ctx = getAudioCtx();
    if (!ctx) {
      gainRef.current = null;
      sourceRef.current = null;
      nextSourceRef.current = null;
      nextGainRef.current = null;
      bufferRef.current = null;
      setIsPlaying(false);
      return;
    }
    clearLoopTimer();
    const now = ctx.currentTime;
    if (gainRef.current && sourceRef.current) {
      gainRef.current.gain.cancelScheduledValues(now);
      gainRef.current.gain.setValueAtTime(gainRef.current.gain.value, now);
      gainRef.current.gain.linearRampToValueAtTime(0, now + FADE_TIME);
      sourceRef.current.stop(now + FADE_TIME + 0.1);
    }
    gainRef.current = null;
    sourceRef.current = null;
    nextSourceRef.current = null;
    nextGainRef.current = null;
    bufferRef.current = null;
    setIsPlaying(false);
  };

  // External hook to align with stems stop
  const stopIfPlaying = () => stopPad();

  const setPadVolume = (val) => {
    setVolume(val);
    const ctx = getAudioCtx();
    if (!ctx) return;
    const now = ctx.currentTime;
    if (gainRef.current) {
      gainRef.current.gain.cancelScheduledValues(now);
      gainRef.current.gain.setValueAtTime(gainRef.current.gain.value, now);
      gainRef.current.gain.linearRampToValueAtTime(val, now + 0.1);
    }
  };

  const setPadPan = (val) => {
    setPan(val);
    if (panNodeRef.current) {
      panNodeRef.current.pan.value = val;
    }
  };

  return {
    isPlaying,
    loading,
    currentKey: currentKey || displayKey || resolveKey(songKey),
    togglePad,
    fadeToKey,
    stopPad,
    stopIfPlaying,
    volume,
    setPadVolume,
    pan,
    setPadPan,
  };
}

export default usePadPlayer;
